import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';

const CONTACT_API_URL =
  process.env.REACT_APP_CONTACT_API || 'https://consoletz.bwitonline.com/api/contact-form';

const Home = () => {
  const navigate = useNavigate();
  const [isBrandModalOpen, setBrandModalOpen] = useState(false);
  const [isDeviceModalOpen, setDeviceModalOpen] = useState(false);
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const [isHeroHovered, setHeroHovered] = useState(false);
  const [statementSlideIndex, setStatementSlideIndex] = useState(0);
  const [isStatementHovered, setStatementHovered] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    company: '',
    contactNumber: '',
    email: '',
    message: '',
  });
  const [contactStatus, setContactStatus] = useState({ type: 'idle', message: '' });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const heroSlides = useMemo(
    () => {
      const baseSlide = {
        title: 'State-of-the-Art Production',
        description: 'Advanced KBA and Heidelberg presses deliver exceptional print quality.'
      };

      // Use updated images from /images/gallery/slider1/Tanzania_1.webp to Tanzania_12.webp
      return Array.from({ length: 12 }, (_, index) => {
        const imageNumber = index + 1; // 1 through 12
        return {
          src: `/images/gallery/slider1/Tanzania_${imageNumber}.webp`,
          ...baseSlide
        };
      });
    },
    []
  );


  const brandIntroParagraph =
    'Parksons Packaging Tanzania Limited is a subsidiary of Parksons Packaging Ltd, India’s largest printed packaging company, and operates a state-of-the-art facility in Dar es Salaam. Equipped with best-in-class KBA and Heidelberg machines—offering Conventional and UV printing, the first of its kind in Tanzania—our plant produces decorated cartons with film lamination, hot foiling, and precise finishing for superior shelf appeal.';

  const brandDetailParagraphs = [
    'We are equipped with automated die-cutting, folding, and gluing lines ensuring accuracy and consistency. We offer in-house pre-press and plate making, with advanced automated workflow from ESKO, for seamless colour management and faster turnaround.',
    'BRC audited and compliant, our facility meets the highest international standards for safety, quality, and hygiene in packaging manufacturing. We uphold transparency, quality governance, and global best practices, trusted by category leaders in Tobacco, FMCG, Food & Beverage, Personal Care, and Chemicals.',
    'With growing momentum in Tanzania, we are expanding exports to Zambia, Malawi, Kenya, Uganda, Mozambique, and South Africa—strengthening our position as East Africa’s trusted printed packaging partner.',
    'Connect with us today and elevate your brand with world-class packaging.'
  ];

  const brandBulletPoints = [
    'Backed by Parksons Packaging India’s six-decade legacy of innovation and quality.',
    'Best-in-class facility in Tanzania offering high-quality offset printed cartons.',
    'Local manufacturing with global quality standards.',
    'Faster turnaround times with lower inventory, working capital, and superior service.',
    'Proven expertise across regulated and high-demand industries.'
  ];

  const deviceIntroParagraph =
    'We serve a diverse set of markets where packaging needs to be reliable, consistent and visually strong. Our work spans categories that demand precision, durability and clean presentation, often across complex supply chains and varied retail environments. Across all segments, the focus remains on well-built structures, sharp print quality and packaging that supports brands in showing up confidently wherever they are sold.';

  const statementSlides = useMemo(
    () => [
      {
        src: '/images/gallery/slider2/Product_Tanzania_1.webp',
        title: 'Design Excellence',
        description: 'Creative teams deliver compelling visual narratives for every brand.'
      },
      {
        src: '/images/gallery/slider2/Product_Tanzania_2.webp',
        title: 'Vision in Motion',
        description: 'Strategic insights translate into packaging that stands out on shelves.'
      },
      {
        src: '/images/gallery/slider2/Product_Tanzania_3.webp',
        title: 'Quality Craftsmanship',
        description: 'Precision and attention to detail in every product we create.'
      }
    ],
    []
  );

  useEffect(() => {
    const anyModalOpen = isBrandModalOpen || isDeviceModalOpen;

    if (!anyModalOpen) {
      document.body.style.overflow = '';
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (isDeviceModalOpen) {
          setDeviceModalOpen(false);
        } else if (isBrandModalOpen) {
          setBrandModalOpen(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isBrandModalOpen, isDeviceModalOpen]);

  useEffect(() => {
    if (isHeroHovered) {
      return undefined;
    }

    const timer = setInterval(() => {
      setHeroSlideIndex((prevIndex) => (prevIndex + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [heroSlides.length, isHeroHovered]);

  useEffect(() => {
    if (isStatementHovered) {
      return undefined;
    }

    const timer = setInterval(() => {
      setStatementSlideIndex((prevIndex) => (prevIndex + 1) % statementSlides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [statementSlides.length, isStatementHovered]);

  const handleContactInputChange = (event) => {
    const { name, value } = event.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    if (isSubmittingContact) return;

    const trimmedName = contactForm.name.trim();
    const trimmedCompany = contactForm.company.trim();
    const trimmedEmail = contactForm.email.trim();
    const trimmedMessage = contactForm.message.trim();

    if (!trimmedName || !trimmedCompany || !trimmedEmail || !trimmedMessage) {
      setContactStatus({
        type: 'error',
        message: 'Please fill in all mandatory fields before submitting.',
      });
      return;
    }

    setIsSubmittingContact(true);
    setContactStatus({ type: 'idle', message: '' });

    try {
      const response = await fetch(CONTACT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmedName,
          company: trimmedCompany,
          contact_number: contactForm.contactNumber.trim(),
          email: trimmedEmail,
          message: trimmedMessage,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to submit. Please try again.');
      }

      setContactStatus({
        type: 'success',
        message: "Thanks for reaching out. We'll get back to you shortly.",
      });
      setContactForm({ name: '', company: '', contactNumber: '', email: '', message: '' });
    } catch (error) {
      console.error('Contact form submission failed:', error);
      setContactStatus({
        type: 'error',
        message: error.message || 'Something went wrong. Please try again later.',
      });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const openBrandModal = () => {
    navigate('/about-ppl-tz');
  };
  const closeBrandModal = () => setBrandModalOpen(false);

  const openDeviceModal = () => {
    navigate('/products-ppl-tz');
  };
  const closeDeviceModal = () => setDeviceModalOpen(false);

  return (
    <div className="home-page">
      {/* Hero Image Section - Outside content container */}
      <div className="hero-image-section">
        <img 
          src="/images/hero/hero-banner.webp" 
          alt="Hero Banner" 
          className="hero-image"
        />
      </div>

      <div className="content-container fade-in">
        <div className="content-container">
          <div className="brand-grid">
            <div
              className="brand-tile brand-tile__hero"
              onMouseEnter={() => setHeroHovered(true)}
              onMouseLeave={() => setHeroHovered(false)}
            >
              <div className="hero-slider">
                {heroSlides.map((slide, index) => (
                  <div
                    key={slide.src}
                    className={`hero-slide ${index === heroSlideIndex ? 'hero-slide--active' : ''}`}
                    aria-hidden={index !== heroSlideIndex}
                  >
                    <img src={slide.src} alt={slide.title} className="hero-slide__image" />
                    <div className="hero-slide__caption">
                      <h2>{slide.title}</h2>
                      <p>{slide.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="brand-tile brand-tile__mark">
              <h3>Our Capabilities</h3>
              <p>{brandIntroParagraph}</p>
              <button
                type="button"
                className="brand-read-more"
                onClick={openBrandModal}
                aria-label="Read more about Parksons Packaging Tanzania"
              >
                Read More
              </button>
            </div>
            <div
              className="brand-tile brand-tile__statement"
              onMouseEnter={() => setStatementHovered(true)}
              onMouseLeave={() => setStatementHovered(false)}
            >
              <div className="statement-slider">
                {statementSlides.map((slide, index) => (
                  <div
                    key={slide.src}
                    className={`statement-slide ${index === statementSlideIndex ? 'statement-slide--active' : ''}`}
                    aria-hidden={index !== statementSlideIndex}
                  >
                    <img src={slide.src} alt={slide.title} className="statement-slide__image" />
                    <div className="statement-slide__caption">
                      <h3>{slide.title}</h3>
                      <p>{slide.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="brand-tile brand-tile__device">
              <h3>Markets Serviced</h3>
              <p>{deviceIntroParagraph}</p>
              <button
                type="button"
                className="brand-read-more"
                onClick={openDeviceModal}
                aria-label="Read more about Product Preview"
              >
                Read More
              </button>
            </div>
          </div>
        </div>
      </div>

      {isBrandModalOpen && (
        <div
          className="brand-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="brand-modal-title"
          id="brand-modal"
        >
          <div className="brand-modal">
            <button
              type="button"
              className="brand-modal-close"
              onClick={closeBrandModal}
              aria-label="Close brand details"
            >
              ×
            </button>
            <h3 id="brand-modal-title">About Parksons Packaging Tanzania</h3>
            <p>{brandIntroParagraph}</p>

            <h4>Why Choose Parksons Packaging Tanzania</h4>
            <ul>
              {brandBulletPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>

            {brandDetailParagraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
      )}

      {isDeviceModalOpen && (
        <div
          className="brand-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="device-modal-title"
          id="device-modal"
        >
          <div className="brand-modal">
            <button
              type="button"
              className="brand-modal-close"
              onClick={closeDeviceModal}
              aria-label="Close facility details"
            >
              ×
            </button>
            <h3 id="device-modal-title">Product Preview</h3>
            <p>{deviceIntroParagraph}</p>
          </div>
        </div>
      )}

      <div id="contact-section" className="contact-section fade-in">
        <div className="contact-container">
          <p className="contact-intro">We're Here for You: Contact Us Today!</p>
          <form className="contact-form" onSubmit={handleContactSubmit}>
            <div className="contact-grid">
              <div className="contact-field">
                <label htmlFor="contact-name">
                  Your Name <span aria-hidden="true">*</span>
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  placeholder="Enter your full name"
                  value={contactForm.name}
                  onChange={handleContactInputChange}
                  autoComplete="name"
                  required
                />
              </div>
              <div className="contact-field">
                <label htmlFor="company-name">
                  Company Name <span aria-hidden="true">*</span>
                </label>
                <input
                  type="text"
                  id="company-name"
                  name="company"
                  placeholder="Enter your company name"
                  value={contactForm.company}
                  onChange={handleContactInputChange}
                  autoComplete="organization"
                  required
                />
              </div>
              <div className="contact-field">
                <label htmlFor="contact-number">Contact Number</label>
                <input
                  type="tel"
                  id="contact-number"
                  name="contactNumber"
                  placeholder="Enter your contact number"
                  value={contactForm.contactNumber}
                  onChange={handleContactInputChange}
                  autoComplete="tel"
                />
              </div>
              <div className="contact-field">
                <label htmlFor="contact-email">
                  Email ID <span aria-hidden="true">*</span>
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  placeholder="Enter your email address"
                  value={contactForm.email}
                  onChange={handleContactInputChange}
                  autoComplete="email"
                  required
                />
              </div>
            </div>
            <div className="contact-field contact-field--full">
              <label htmlFor="contact-message">
                Message <span aria-hidden="true">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                placeholder="Tell us how we can help you"
                value={contactForm.message}
                onChange={handleContactInputChange}
                required
                rows={5}
              />
            </div>
            <button type="submit" className="contact-submit" disabled={isSubmittingContact}>
              {isSubmittingContact ? 'Sending...' : 'Submit'}
            </button>
            {contactStatus.message && (
              <p
                className={`contact-alert contact-alert--${contactStatus.type}`}
                role="status"
                aria-live="polite"
              >
                {contactStatus.message}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Home;
